import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiErr } from "@/lib/api";
import { WEEKDAYS, fmtDateTime } from "@/lib/format";

export default function Configuracion() {
  return (
    <div>
      <PageHeader title="Configuración" subtitle="Todo es autoadministrable" />
      <Tabs defaultValue="empresa">
        <TabsList className="flex-wrap">
          <TabsTrigger value="empresa" data-testid="tab-empresa">Empresa</TabsTrigger>
          <TabsTrigger value="areas" data-testid="tab-areas">Áreas</TabsTrigger>
          <TabsTrigger value="turnos" data-testid="tab-turnos">Turnos</TabsTrigger>
          <TabsTrigger value="tipos" data-testid="tab-tipos">Tipos equipo</TabsTrigger>
          <TabsTrigger value="acciones" data-testid="tab-acciones">Acciones correctivas</TabsTrigger>
          <TabsTrigger value="auditoria" data-testid="tab-auditoria">Auditoría</TabsTrigger>
          <TabsTrigger value="demo" data-testid="tab-demo">Datos demo</TabsTrigger>
        </TabsList>
        <TabsContent value="empresa"><Empresa /></TabsContent>
        <TabsContent value="areas"><Areas /></TabsContent>
        <TabsContent value="turnos"><Turnos /></TabsContent>
        <TabsContent value="tipos"><SimpleCatalog endpoint="/equipment-types" label="tipo de equipo" testid="type" /></TabsContent>
        <TabsContent value="acciones"><SimpleCatalog endpoint="/corrective-action-types" label="acción correctiva" testid="ca" /></TabsContent>
        <TabsContent value="auditoria"><Auditoria /></TabsContent>
        <TabsContent value="demo"><Demo /></TabsContent>
      </Tabs>
    </div>
  );
}

function Empresa() {
  const [org, setOrg] = useState(null);
  useEffect(() => { api.get("/org").then(({ data }) => setOrg(data)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/org", { name: org.name, legal_name: org.legal_name, tax_id: org.tax_id, country: org.country, timezone: org.timezone }); toast.success("Empresa actualizada"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!org) return <div className="py-8 text-slate-400">Cargando…</div>;
  return (
    <div className="mt-4 max-w-xl space-y-4 rounded-2xl border border-slate-200 bg-white p-6">
      {[["Nombre comercial", "name"], ["Razón social", "legal_name"], ["RUT / ID fiscal", "tax_id"], ["País", "country"], ["Zona horaria", "timezone"]].map(([l, k]) => (
        <div key={k}><Label className="text-sm">{l}</Label><Input data-testid={`org-${k}`} value={org[k] || ""} onChange={(e) => setOrg({ ...org, [k]: e.target.value })} className="mt-1 h-11" /></div>
      ))}
      <Button data-testid="save-org-btn" onClick={save} className="rounded-xl">Guardar</Button>
    </div>
  );
}

function Areas() {
  const [locations, setLocations] = useState([]);
  const [loc, setLoc] = useState("");
  const [areas, setAreas] = useState([]);
  const [name, setName] = useState("");
  useEffect(() => { api.get("/locations").then(({ data }) => { setLocations(data); if (data[0]) setLoc(data[0].id); }).catch(() => {}); }, []);
  const load = () => { if (loc) api.get(`/areas?location_id=${loc}`).then(({ data }) => setAreas(data)).catch(() => {}); };
  useEffect(() => { load(); }, [loc]);
  const add = async () => { if (!name) return; try { await api.post("/areas", { location_id: loc, name }); setName(""); toast.success("Área creada"); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="mt-4 max-w-xl space-y-4">
      <Select value={loc} onValueChange={setLoc}><SelectTrigger data-testid="areas-loc" className="h-11 bg-white"><SelectValue placeholder="Local" /></SelectTrigger>
        <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>
      <div className="flex gap-2"><Input data-testid="area-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nueva área (ej. Cocina)" className="h-11" /><Button data-testid="add-area-btn" onClick={add} className="rounded-xl">Agregar</Button></div>
      <div className="rounded-2xl border border-slate-200 bg-white">{areas.map((a) => <div key={a.id} className="border-b border-slate-100 p-3 text-sm last:border-0">{a.name}</div>)}</div>
    </div>
  );
}

function Turnos() {
  const [locations, setLocations] = useState([]);
  const [loc, setLoc] = useState("");
  const [shifts, setShifts] = useState([]);
  const [form, setForm] = useState({ name: "", reference_start_time: "08:00", tolerance_minutes: 45, weekdays: [1, 2, 3, 4, 5, 6, 7] });
  useEffect(() => { api.get("/locations").then(({ data }) => { setLocations(data); if (data[0]) setLoc(data[0].id); }).catch(() => {}); }, []);
  const load = () => { if (loc) api.get(`/shifts?location_id=${loc}`).then(({ data }) => setShifts(data)).catch(() => {}); };
  useEffect(() => { load(); }, [loc]);
  const toggleDay = (d) => setForm((f) => ({ ...f, weekdays: f.weekdays.includes(d) ? f.weekdays.filter((x) => x !== d) : [...f.weekdays, d] }));
  const add = async () => {
    if (!form.name) { toast.error("Nombre del turno requerido"); return; }
    try { await api.post("/shifts", { location_id: loc, ...form, tolerance_minutes: Number(form.tolerance_minutes), active: true }); setForm({ name: "", reference_start_time: "08:00", tolerance_minutes: 45, weekdays: [1, 2, 3, 4, 5, 6, 7] }); toast.success("Turno creado"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="mt-4 max-w-xl space-y-4">
      <Select value={loc} onValueChange={setLoc}><SelectTrigger data-testid="turnos-loc" className="h-11 bg-white"><SelectValue placeholder="Local" /></SelectTrigger>
        <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-1"><Label className="text-xs">Nombre</Label><Input data-testid="shift-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-10" placeholder="Turno Mañana" /></div>
          <div><Label className="text-xs">Hora apertura</Label><Input data-testid="shift-start" type="time" value={form.reference_start_time} onChange={(e) => setForm({ ...form, reference_start_time: e.target.value })} className="mt-1 h-10" /></div>
          <div><Label className="text-xs">Tolerancia (min)</Label><Input data-testid="shift-tol" type="number" value={form.tolerance_minutes} onChange={(e) => setForm({ ...form, tolerance_minutes: e.target.value })} className="mt-1 h-10 tnum" /></div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {WEEKDAYS.map((d) => (
            <button key={d.value} data-testid={`shift-day-${d.value}`} onClick={() => toggleDay(d.value)} className={`rounded-lg px-2.5 py-1 text-xs font-medium ${form.weekdays.includes(d.value) ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-500"}`}>{d.label}</button>
          ))}
        </div>
        <Button data-testid="add-shift-btn" onClick={add} className="rounded-xl">Crear turno</Button>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white">
        {shifts.map((s) => <div key={s.id} className="border-b border-slate-100 p-3 text-sm last:border-0"><b>{s.name}</b> · {s.reference_start_time} · tol {s.tolerance_minutes}min · {s.weekdays?.length || 0} días</div>)}
      </div>
    </div>
  );
}

function SimpleCatalog({ endpoint, label, testid }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get(endpoint).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => { if (!name) return; try { await api.post(endpoint, { name }); setName(""); toast.success("Creado"); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="mt-4 max-w-xl space-y-4">
      <div className="flex gap-2"><Input data-testid={`${testid}-name`} value={name} onChange={(e) => setName(e.target.value)} placeholder={`Nuevo ${label}`} className="h-11" /><Button data-testid={`add-${testid}-btn`} onClick={add} className="rounded-xl">Agregar</Button></div>
      <div className="rounded-2xl border border-slate-200 bg-white">{items.map((t) => <div key={t.id} className="flex items-center justify-between border-b border-slate-100 p-3 text-sm last:border-0">{t.name}{!t.organization_id && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-400">Global</span>}</div>)}</div>
    </div>
  );
}

function Auditoria() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/audit-logs?limit=200").then(({ data }) => setLogs(data)).catch(() => {}); }, []);
  return (
    <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white">
      {logs.length === 0 ? <p className="p-6 text-center text-sm text-slate-400">Sin registros de auditoría.</p> :
        logs.map((l) => <div key={l.id} className="flex items-center justify-between border-b border-slate-100 p-3 text-sm last:border-0"><span><b>{l.action}</b> <span className="text-slate-400">· {l.entity_type}</span></span><span className="text-xs text-slate-400">{fmtDateTime(l.created_at)}</span></div>)}
    </div>
  );
}

function Demo() {
  const reset = async () => {
    try { await api.post("/demo/reset"); toast.success("Datos demo reiniciados"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="mt-4 max-w-xl rounded-2xl border border-slate-200 bg-white p-6">
      <p className="font-display text-lg font-semibold">Datos demo</p>
      <p className="mt-1 text-sm text-slate-500">Reinicia la organización demo (Demo Restaurant) a su estado original. No afecta a empresas reales.</p>
      <Button data-testid="reset-demo-btn" variant="outline" onClick={reset} className="mt-4 rounded-xl">Reiniciar datos demo</Button>
    </div>
  );
}
